<template>
  <div class="personal-info-container">
    <!-- 标题 -->
    <div class="title">品牌喜好</div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <div class="data-card">
        <div class="form-content">
          <!-- 提示信息 -->
          <div class="hint-text">
            <span v-if="selectedBrands.length === 0" class="hint-default">请选择 1-10 个品牌</span>
            <span v-else-if="selectedBrands.length > 10" class="hint-warning">最多选择 10 个品牌 ({{ selectedBrands.length }}/10)</span>
            <span v-else class="hint-success">已选择 {{ selectedBrands.length }} 个品牌</span>
          </div>

          <!-- 品牌选择网格 -->
          <div class="brands-grid">
            <div 
              v-for="(brand, index) in availableBrands" 
              :key="index" 
              class="brand-card"
              :class="{ selected: selectedBrands.includes(brand.name) }"
              @click="toggleBrand(brand.name)"
            >
              <div class="brand-name">{{ brand.name }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 底部操作按钮 -->
    <div class="bottom-actions">
      <button class="btn-secondary" @click="goBack">
        <span class="btn-icon"><img :src="backIcon"></span>
        <span class="btn-text">上一步</span>
      </button>
      <button 
        class="btn-primary" 
        :class="{ disabled: !isFormValid || isLoading, loading: isLoading }"
        :disabled="!isFormValid || isLoading"
        @click="handleSubmit"
      >
        <!-- 加载状态 -->
        <div v-if="isLoading" class="loading-content">
          <div class="loading-spinner"></div>
          <div class="loading-text">处理中...</div>
        </div>
        <!-- 正常状态 -->
        <div v-else class="button-content">
          <span class="btn-icon"><img :src="confirmIcon"></span>
          <span class="btn-text">确定</span>
        </div>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue'
const confirmIcon = require('@/assets/images/svg/confirm.svg')
const backIcon = require('@/assets/images/svg/back.svg')

// 定义 emits
const emit = defineEmits(['next', 'back', 'validation-change', 'data-change'])

// 定义 props 来接收回填数据
const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({})
  },
  step1Data: {
    type: Object,
    default: () => ({})
  },
  step2Data: {
    type: Object,
    default: () => ({})
  },
  step3Data: {
    type: Object,
    default: () => ({})
  }
})

// 可选择的品牌列表
const availableBrands = ref([
  { name: 'THE ROW', logo: null },
  { name: 'Burberry', logo: null },
  { name: 'Ralph Lauren', logo: null },
  { name: 'Maison Margiela', logo: null },
  { name: 'Acne Studios', logo: null },
    { name: 'SKIMS', logo: null },
  { name: 'COS', logo: null },
  { name: 'Adidas Originals', logo: null },
  { name: 'The North Face', logo: null },
  { name: 'JACQUEMUS', logo: null },

])

// 响应式数据（支持回填）
const selectedBrands = ref(
  props.initialData && props.initialData.favoriteBrands && Array.isArray(props.initialData.favoriteBrands)
    ? [...props.initialData.favoriteBrands]
    : []
)
const isLoading = ref(false)

// 计算属性 - 验证是否可以提交
const isFormValid = computed(() => {
  return selectedBrands.value.length >= 1 && selectedBrands.value.length <= 10
})

// 监听验证状态变化并通知父组件
watch(isFormValid, (newValue) => {
  nextTick(() => {
    emit('validation-change', newValue)
  })
})

// 监听props变化，更新本地数据
watch(() => props.initialData, (newInitialData) => {
  if (newInitialData) {
    if (newInitialData.favoriteBrands && Array.isArray(newInitialData.favoriteBrands)) {
      selectedBrands.value = [...newInitialData.favoriteBrands]
      console.log('Step4: 从initialData恢复品牌选择:', selectedBrands.value)
    }
  }
}, { deep: true, immediate: true })

// 组件挂载后手动触发一次验证状态检查
onMounted(() => {
  // 从 props.initialData 恢复品牌选择
  if (props.initialData && props.initialData.favoriteBrands && Array.isArray(props.initialData.favoriteBrands)) {
    selectedBrands.value = [...props.initialData.favoriteBrands]
    console.log('Step4 mounted: 恢复品牌选择:', selectedBrands.value)
  }
  
  nextTick(() => {
    emit('validation-change', isFormValid.value)
    // 如果有初始选择的品牌，也要触发数据保存
    if (selectedBrands.value.length > 0) {
      emit('data-change', {
        favoriteBrands: [...selectedBrands.value]
      })
    }
  })
})

// 切换品牌选择状态
const toggleBrand = (brandName) => {
  const index = selectedBrands.value.indexOf(brandName)
  if (index > -1) {
    // 如果已选中，则取消选择
    selectedBrands.value.splice(index, 1)
  } else {
    // 如果未选中且未达到上限，则添加选择
    if (selectedBrands.value.length < 10) {
      selectedBrands.value.push(brandName)
    }
  }
  
  // 每次选择变化时，通知父组件保存数据
  emit('data-change', {
    favoriteBrands: [...selectedBrands.value]
  })
}

// 返回上一步
const goBack = () => {
  // 在返回前确保保存当前选择的品牌数据
  emit('data-change', {
    favoriteBrands: [...selectedBrands.value]
  })
  // 延迟一下再返回，确保数据保存完成
  nextTick(() => {
    emit('back')
  })
}

// 提交表单
const handleSubmit = () => {
  if (!isFormValid.value) return
  
  const stepData = {
    favoriteBrands: [...selectedBrands.value]
  }
  
  emit('next', stepData)
}

// 暴露方法给父组件调用
defineExpose({
  handleSubmit
})
</script>

<style scoped>
/* 主容器 - 全屏竖屏布局 */
.personal-info-container {
  width: 100vw;
  height: 100dvh;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
}

/* 标题样式 */
.title {
  color: rgba(255, 255, 255, 0.95);
  font-size: 24px;
  font-weight: 1000;
  margin-top: 10%;
  margin-bottom: 30px;
  letter-spacing: 0.5px;
  line-height: 1.2;
  text-align: center;
  position: relative;
  z-index: 1;
  flex-shrink: 0;
}

/* 主要内容区域 */
.main-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: calc(100% - 32px);
  max-width: 420px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  flex: 1;
  max-height: calc(100dvh - 200px);
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(166, 245, 93, 0.3) transparent;
}

.main-content::-webkit-scrollbar {
  width: 4px;
}

.main-content::-webkit-scrollbar-track {
  background: transparent;
}

.main-content::-webkit-scrollbar-thumb {
  background: rgba(166, 245, 93, 0.3);
  border-radius: 2px;
}

.main-content::-webkit-scrollbar-thumb:hover {
  background: rgba(166, 245, 93, 0.5);
}

/* 数据卡片 */
.data-card {
  width: 100%;
  background: rgba(35, 35, 35, 0.92);
  border-radius: 24px;
  padding: 28px 24px;
  backdrop-filter: blur(20px);
  display: flex;
  flex-direction: column;
  position: relative;
  isolation: isolate;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.data-card::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 24px;
  padding: 1px;
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 1), 
    rgba(204, 204, 204, 0.8), 
    rgba(204, 204, 204, 0.4), 
    rgba(204, 204, 204, 0.4), 
    rgba(204, 204, 204, 0.8), 
    rgba(255, 255, 255, 0.9));
  -webkit-mask: 
    linear-gradient(#fff 0 0) content-box, 
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  mask: 
    linear-gradient(#fff 0 0) content-box, 
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  pointer-events: none;
  z-index: -1;
}

.data-card > * {
  position: relative;
  z-index: 1;
}

/* 表单内容区域 */
.form-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: stretch;
}

/* 提示信息 */
.hint-text {
  font-size: 14px;
  text-align: center;
  margin-bottom: 12px;
  font-weight: 400;
  letter-spacing: 0.3px;
  transition: all 0.3s ease;
}

.hint-default {
  color: rgba(255, 255, 255, 0.65);
}

.hint-success {
  color: #A6F55D;
  font-weight: 500;
}

.hint-warning {
  color: #FFB84D;
  font-weight: 500;
}

/* 品牌选择网格 - 流式布局 */
.brands-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  padding: 0;
  justify-content: flex-start;
  align-items: flex-start;
}

/* 品牌卡片 - 椭圆胶囊按钮样式 */
.brand-card {
  width: auto;
  min-width: fit-content;
  height: 44px;
  background: #B3B3B3;
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.15);
  border: none;
  border-radius: 22px; /* 椭圆形 */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: visible;
  padding: 0 18px;
  flex-shrink: 0;
}

.brand-card:hover {
  background: #C4C4C4;
  transform: translateY(-2px);
  box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.2);
}

.brand-card:active {
  transform: translateY(0);
}

.brand-card.selected {
  background: rgba(255, 255, 255, 0.98);
  border: none;
  box-shadow: 0px 3px 12px 0px rgba(0, 0, 0, 0.18);
  transform: translateY(-1px);
}

.brand-card.selected:hover {
  background: rgba(255, 255, 255, 1);
  box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.22);
  transform: translateY(-2px);
}

/* 品牌名称 */
.brand-name {
  font-size: 14px;
  font-weight: normal;
  text-align: center;
  white-space: nowrap;
  line-height: 1;
}

.brand-card.selected .brand-name {
  color: #262626;
  font-weight: 1000;
}

/* 底部操作按钮 */
.bottom-actions {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 5%;
  padding: 16px 20px calc(16px + env(safe-area-inset-bottom, 0px));
  display: flex;
  gap: 12px;
  justify-content: space-between;
  /*background: linear-gradient(180deg, rgba(10,10,10,0) 0%, rgba(10,10,10,0.8) 40%, rgba(10,10,10,0.95) 100%);*/
  /*backdrop-filter: blur(16px);*/
  z-index: 100;
  pointer-events: none;
}

.bottom-actions > * {
  pointer-events: auto;
}

.btn-secondary, .btn-primary {
  flex: 1;
  height: 48px;
  border-radius: 24px;
  border: none;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-secondary { 
  background: rgba(255, 255, 255, 0.12); 
  color: rgba(255, 255, 255, 0.95); 
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.18);
}

.btn-secondary:active {
  transform: scale(0.98);
}

.btn-primary { 
  background: #A6F55D; 
  color: #111; 
  font-weight: 600;
  box-shadow: 0 4px 16px rgba(166, 245, 93, 0.3);
}

.btn-primary:hover:not(.disabled) {
  background: #95E04C;
}

.btn-primary:active:not(.disabled) {
  transform: scale(0.98);
}

.btn-primary.disabled { 
  opacity: 0.4; 
  cursor: not-allowed;
  box-shadow: none;
}

.btn-icon {
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-icon img {
  display: block;
  width: 20px;
  height: 20px;
  vertical-align: middle;
}

.btn-text {
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  line-height: 1;
}

/* 按钮内容容器 */
.button-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 加载状态样式 */
.loading-content {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: center;
}

.loading-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid rgba(255, 255, 255, 1);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.loading-text {
  color: rgba(255, 255, 255, 0.95);
  font-size: 14px;
  font-weight: 500;
}

/* 品牌卡片选中动画 */
@keyframes brandSelect {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1) translateY(-1px);
  }
}

.brand-card.selected {
  animation: brandSelect 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* 小屏幕适配 */
@media (max-width: 375px) {
  .brands-grid {
    gap: 8px;
  }
  
  .brand-card {
    height: 40px;
    border-radius: 20px;
    padding: 0 15px;
  }
  
  .brand-name {
    font-size: 13px;
  }
}

/* 超小屏幕适配 */
@media (max-width: 320px) {
  .brands-grid {
    gap: 7px;
  }
  
  .brand-card {
    height: 38px;
    border-radius: 19px;
    padding: 0 13px;
  }
  
  .brand-name {
    font-size: 12px;
  }
}
</style>

